import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, useParams } from "react-router";
import { ArrowLeft, Code2, Eye } from "lucide-react";
import { toast } from "sonner";
import { BrandSiteIdeFileTree } from "@/components/brand-site/BrandSiteIdeFileTree";
import { BrandSiteLivePreview } from "@/components/brand-site/BrandSiteLivePreview";
import { HermesDrawer } from "@/components/hermes/HermesDrawer";
import { HermesProvider, useHermes } from "@/components/hermes/HermesProvider";
import { AdminAlert } from "@/components/shell/AdminPage";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ApiError } from "@/lib/api";
import {
  BrandSiteApiError,
  brandSiteAssetBaseUrl,
  brandSiteVisitUrl,
  listBrandSitePages,
  publishBrandSite,
  updateBrandSitePage,
  type BrandSitePage,
} from "@/lib/brand-site-api";
import {
  platformTemplateIdeKbBindings,
  type PlatformTemplateIdeContext,
} from "@/lib/hermes-platform-template-ide";
import { createKbApi } from "@/lib/kb-api";
import { fetchPlatformTemplates, type PlatformTemplateRow } from "@/lib/platform-api";
import { useRefreshBrandSitePagesOnHermesDraft } from "@/lib/use-refresh-brand-site-pages-on-hermes-draft";
import { cn } from "@/lib/utils";

const MonacoEditor = lazy(() => import("@monaco-editor/react"));

type CenterTab = "code" | "preview";

type LoadedTemplate = {
  row: PlatformTemplateRow;
  accountId: string;
  brandId: number;
  brandSiteId: number;
};

/** 平台模板 IDE：左树 / 中 Monaco↔预览 / 右 Hermes（自带 Provider）。 */
export default function PlatformTemplateIdePage() {
  const { sourceKey: rawSourceKey } = useParams();
  const sourceKey = rawSourceKey ? decodeURIComponent(rawSourceKey) : "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<LoadedTemplate | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!sourceKey) {
        setError("缺少模板标识");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPlatformTemplates();
        if (cancelled) return;
        const row = (result.templates ?? []).find((item) => item.source_key === sourceKey) ?? null;
        if (!row?.brand_site?.id) {
          setError("该模板尚未挂站，无法打开 IDE");
          setLoaded(null);
          return;
        }
        const accountId = String(row.brand_site.account_id ?? "");
        const brandId = Number(row.brand_site.brand_id);
        if (!accountId || !Number.isFinite(brandId) || brandId <= 0) {
          setError("模板站缺少 account_id / brand_id，请确认品牌站 API 返回完整字段");
          setLoaded(null);
          return;
        }
        setLoaded({
          row,
          accountId,
          brandId,
          brandSiteId: row.brand_site.id,
        });
      } catch (caught) {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : "加载平台模板失败");
        setLoaded(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sourceKey]);

  if (loading) {
    return <p className="text-sm text-muted-foreground">加载模板 IDE…</p>;
  }

  if (error || !loaded) {
    return (
      <div className="space-y-3">
        <Button type="button" variant="ghost" size="sm" asChild>
          <Link to="/platform/templates">
            <ArrowLeft className="size-4" aria-hidden />
            返回模板列表
          </Link>
        </Button>
        <AdminAlert>{error ?? "无法打开 IDE"}</AdminAlert>
      </div>
    );
  }

  return (
    <HermesProvider accountIdOverride={loaded.accountId}>
      <PlatformTemplateIdeWorkspace loaded={loaded} />
    </HermesProvider>
  );
}

function PlatformTemplateIdeWorkspace({ loaded }: { loaded: LoadedTemplate }) {
  const { accountId, brandId, brandSiteId, row } = loaded;
  const {
    preparePlatformTemplateIde,
    startPlatformTemplateIdeSession,
    stopPlatformTemplateIde,
    isSending,
    brandSiteDraftRevision,
  } = useHermes();

  const [pages, setPages] = useState<BrandSitePage[]>([]);
  const [pagesError, setPagesError] = useState<string | null>(null);
  const [kbHint, setKbHint] = useState<string | null>(null);
  const [enterpriseWikiId, setEnterpriseWikiId] = useState<number | null>(null);
  const [brandWikiId, setBrandWikiId] = useState<number | null>(null);
  const [writeAccess, setWriteAccess] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [centerTab, setCenterTab] = useState<CenterTab>("preview");
  const [htmlDraft, setHtmlDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [hermesCollapsed, setHermesCollapsed] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState<string | null>(row.public_url);
  const dirtyRef = useRef(false);
  const lastSyncedPageIdRef = useRef<number | null>(null);
  dirtyRef.current = dirty;

  const selectedPage = useMemo(
    () => pages.find((page) => page.slug === selectedSlug) ?? null,
    [pages, selectedSlug],
  );

  const refreshPages = useCallback(async () => {
    const payload = await listBrandSitePages(accountId, brandSiteId);
    const next = payload.pages ?? [];
    setPages(next);
    return next;
  }, [accountId, brandSiteId]);

  const buildIdeContext = useCallback(
    (opts: {
      enterpriseId?: number | null;
      brandWiki?: number | null;
      write?: boolean;
    }): PlatformTemplateIdeContext => {
      const kbBindings = platformTemplateIdeKbBindings({
        enterpriseWikiId: opts.enterpriseId ?? enterpriseWikiId,
        brandWikiId: opts.brandWiki ?? brandWikiId,
        writeAccess: opts.write ?? writeAccess,
      });
      return {
        mode: "platform_template_ide",
        accountId,
        brandId,
        brandSiteId,
        kbBindings,
      };
    },
    [accountId, brandId, brandSiteId, brandWikiId, enterpriseWikiId, writeAccess],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setPagesError(null);
      try {
        const nextPages = await refreshPages();
        if (cancelled) return;
        const home = nextPages.find((page) => page.is_home) ?? nextPages[0] ?? null;
        setSelectedSlug((current) => current ?? home?.slug ?? null);

        let enterpriseId: number | null = null;
        let brandWiki: number | null = null;
        try {
          const { wikis } = await createKbApi(accountId).listWikis();
          if (cancelled) return;
          enterpriseId = wikis.find((wiki) => wiki.kind === "enterprise")?.id ?? null;
          brandWiki =
            wikis.find(
              (wiki) => wiki.kind === "brand" && String(wiki.brand_id) === String(brandId),
            )?.id ?? null;
          setEnterpriseWikiId(enterpriseId);
          setBrandWikiId(brandWiki);
          if (!enterpriseId || !brandWiki) {
            setKbHint("缺少企业/品牌知识库");
          } else {
            setKbHint(null);
          }
        } catch {
          if (!cancelled) setKbHint("缺少企业/品牌知识库");
        }

        const ctx = buildIdeContext({
          enterpriseId,
          brandWiki,
          write: false,
        });
        preparePlatformTemplateIde(ctx);
        await startPlatformTemplateIdeSession(ctx);
      } catch (caught) {
        if (cancelled) return;
        setPagesError(
          caught instanceof BrandSiteApiError ? caught.message : "加载页面列表失败",
        );
      }
    })();

    return () => {
      cancelled = true;
      stopPlatformTemplateIde();
    };
    // 仅挂载/站点切换时初始化 Hermes；writeAccess 由开关单独处理
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount bootstrap
  }, [accountId, brandId, brandSiteId]);

  useEffect(() => {
    if (!selectedPage) {
      setHtmlDraft("");
      setDirty(false);
      lastSyncedPageIdRef.current = null;
      return;
    }
    const pageChanged = lastSyncedPageIdRef.current !== selectedPage.id;
    if (pageChanged) {
      lastSyncedPageIdRef.current = selectedPage.id;
      setHtmlDraft(selectedPage.html_content);
      setDirty(false);
      return;
    }
    // Hermes 写稿刷新服务端内容时，保留本地未保存草稿
    if (dirtyRef.current) return;
    setHtmlDraft(selectedPage.html_content);
  }, [selectedPage?.id, selectedPage?.html_content]);

  useRefreshBrandSitePagesOnHermesDraft({
    enabled: Boolean(brandSiteId),
    isSending,
    brandSiteDraftRevision,
    refresh: refreshPages,
  });

  async function handleSave() {
    if (!selectedPage) return;
    setSaving(true);
    try {
      const payload = await updateBrandSitePage(accountId, brandSiteId, selectedPage.id, {
        html_content: htmlDraft,
        source: "human",
      });
      setPages((current) =>
        current.map((page) => (page.id === payload.page.id ? payload.page : page)),
      );
      setDirty(false);
      toast.success("已保存草稿");
    } catch (caught) {
      const message = caught instanceof BrandSiteApiError ? caught.message : "保存失败";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleWriteAccessChange(next: boolean) {
    const previous = writeAccess;
    setWriteAccess(next);
    const ctx = buildIdeContext({ write: next });
    preparePlatformTemplateIde(ctx);
    const result = await startPlatformTemplateIdeSession(ctx);
    if (result.syncFailed) {
      setWriteAccess(previous);
      const rollback = buildIdeContext({ write: previous });
      preparePlatformTemplateIde(rollback);
      await startPlatformTemplateIdeSession(rollback);
    }
  }

  async function handlePublish() {
    setPublishing(true);
    try {
      const payload = await publishBrandSite(accountId, brandSiteId);
      const url = brandSiteVisitUrl(payload.brand_site);
      setPublicUrl(url);
      toast.success(url ? `已发布 · ${url.replace(/^https:\/\//, "").split("?")[0]}` : "已发布");
    } catch (caught) {
      const message = caught instanceof BrandSiteApiError ? caught.message : "发布失败";
      toast.error(message);
    } finally {
      setPublishing(false);
    }
  }

  const previewPages = useMemo(() => {
    if (!selectedPage || !dirty) return pages;
    return pages.map((page) =>
      page.id === selectedPage.id ? { ...page, html_content: htmlDraft } : page,
    );
  }, [dirty, htmlDraft, pages, selectedPage]);

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
      <header className="flex shrink-0 items-center gap-3 border-b border-border bg-background px-4 py-2">
        <Button type="button" variant="ghost" size="sm" asChild>
          <Link to="/platform/templates">
            <ArrowLeft className="size-4" aria-hidden />
            模板
          </Link>
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold tracking-tight">
            {row.name}
            <span className="ml-2 font-mono text-xs font-normal text-muted-foreground">
              {row.source_key}
            </span>
          </h1>
          <p className="truncate text-xs text-muted-foreground">
            平台模板 IDE · 草稿预览（非公网）
            {kbHint ? ` · ${kbHint}` : ""}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch
            size="sm"
            checked={writeAccess}
            onCheckedChange={(checked) => void handleWriteAccessChange(checked)}
            aria-label="允许写入知识库"
          />
          允许写入知识库
        </label>
        {publicUrl ? (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-primary-readable hover:underline"
          >
            打开公网
          </a>
        ) : null}
        <Button
          type="button"
          size="sm"
          disabled={publishing || pages.length === 0}
          onClick={() => void handlePublish()}
        >
          {publishing ? "发布中…" : publicUrl ? "重新发布" : "发布"}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setHermesCollapsed((value) => !value)}
        >
          {hermesCollapsed ? "展开 Hermes" : "收起 Hermes"}
        </Button>
      </header>

      {pagesError ? (
        <div className="px-4 py-2">
          <AdminAlert>{pagesError}</AdminAlert>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside className="flex min-h-0 w-56 shrink-0 flex-col overflow-hidden border-r border-border bg-muted/20">
          <BrandSiteIdeFileTree
            pages={pages}
            selectedSlug={selectedSlug}
            onSelectSlug={setSelectedSlug}
          />
        </aside>

        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-1.5">
            <div className="flex items-center gap-1 rounded-md border border-border p-0.5">
              <TabButton
                active={centerTab === "preview"}
                icon={<Eye className="size-3.5" aria-hidden />}
                label="预览"
                onClick={() => setCenterTab("preview")}
              />
              <TabButton
                active={centerTab === "code"}
                icon={<Code2 className="size-3.5" aria-hidden />}
                label="源代码"
                title="高级：内容改动请用右侧 Hermes；一般不必手改源码"
                onClick={() => setCenterTab("code")}
              />
            </div>
            <div className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
              {centerTab === "preview"
                ? "看草稿效果；改内容请对右侧 Hermes 说"
                : selectedPage
                  ? `/pages/${selectedPage.slug}.html`
                  : "未选择文件"}
              {dirty ? " · 未保存" : ""}
            </div>
            {centerTab === "code" ? (
              <Button
                type="button"
                size="sm"
                disabled={!selectedPage || saving || !dirty}
                onClick={() => void handleSave()}
              >
                {saving ? "保存中…" : "保存草稿"}
              </Button>
            ) : null}
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {centerTab === "code" ? (
              selectedPage ? (
                <Suspense
                  fallback={<p className="p-4 text-sm text-muted-foreground">编辑器加载中…</p>}
                >
                  <MonacoEditor
                    height="100%"
                    language="html"
                    theme="vs"
                    value={htmlDraft}
                    onChange={(value) => {
                      const next = value ?? "";
                      setHtmlDraft(next);
                      setDirty(next !== (selectedPage?.html_content ?? ""));
                    }}
                    options={{
                      minimap: { enabled: false },
                      wordWrap: "on",
                      fontSize: 13,
                      automaticLayout: true,
                    }}
                  />
                </Suspense>
              ) : (
                <p className="p-4 text-sm text-muted-foreground">从左侧选择一个页面开始编辑。</p>
              )
            ) : (
              <div className="flex h-full min-h-0 flex-col p-2">
                <BrandSiteLivePreview
                  pages={previewPages}
                  selectedSlug={selectedSlug}
                  draftRevision={brandSiteDraftRevision}
                  assetBaseUrl={brandSiteAssetBaseUrl({
                    public_url: publicUrl,
                    slug: row.brand_site?.slug ?? null,
                    custom_host: null,
                  })}
                  onSelectSlug={setSelectedSlug}
                />
              </div>
            )}
          </div>
        </section>

        {!hermesCollapsed ? (
          <aside className="flex min-h-0 w-[380px] shrink-0 flex-col overflow-hidden border-l border-border">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <HermesDrawer variant="embedded" />
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function TabButton({
  active,
  icon,
  label,
  title,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  label: string;
  title?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      className={cn(
        "inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors",
        active ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
      )}
      aria-pressed={active}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}
